<template>
	<div ref="navRef" class="nav select-none overscroll-none print:hidden" tabindex="-1">
		<CNavHeader
			v-if="!isMobile"
			:current-org="organizationMeta"
			:logo-url="orgThemeLogo"
			:is-collapsed="true"
			:inert="isOpen || showOverlay"
			class="absolute"
			@toggle-open="toggleOpen"
			@show-popper="showPopper"
			@hide-popper="hidePopper"
			@show-account-menu="$emit('show-account-menu')"
		/>
		<template v-if="!isMobile">
			<div
				v-show="!isPopper && !isOpen"
				class="nav__hover-expand h-100 absolute"
				@mouseenter="handleHoverExpandMouseEnter"
				@mouseleave="handleHoverExpandMouseLeave"
			/>
		</template>
		<div
			id="side-menu"
			:inert="!isPopper && !isOpen"
			class="nav__side-menu h-100 shrink-0 flex flex-col"
			:class="{
				'bg-shade-2' : !isMobile,
				'nav__side-menu--mobile' : isMobile
			}"
			@mouseenter="handleMouseEnter"
			@mouseleave="handleMouseLeave"
		>
			<template v-if="!isMobile">
				<div tabindex="-1" class="nav__drag-handle h-100 absolute" />
				<div v-if="isPopper" class="nav__grace-area h-100 absolute" />
			</template>
			<div
				class="nav__sticky top-0 pb-3 shrink-0"
				:class="{ 'bg-shade-2 nav__sticky--align' : !isMobile }"
			>
				<CNavHeader
					:avatar="globalStore.avatar"
					:logo-url="orgThemeLogo"
					:is-collapsed="false"
					:inert="!isOpen && !showOverlay && !isPopper"
					:current-org="organizationMeta"
					class="pb-1"
					@toggle-open="toggleOpen"
					@close-popper="closePopper"
					@show-account-menu="$emit('show-account-menu')"
				/>
				<CNavMain
					class="px-4 py-1"
					:org-code="globalStore.organization.Code"
					:is-org-owner="isOrgOwner"
					:is-org-admin="isOrgAdmin"
					:is-paid-plan="globalStore.organization.Plan.IsPaidPlan"
					:can-use-tasks="globalStore.organization.Plan.AllowWorkflowTasks"
					:task-count="globalStore.totalTaskCount"
					:expand-collapse-option="expandCollapseOption"
					:hide-expand-and-collapse="!hasVisibleFolders"
					:allow-user-to-search="allowUserToSearch"
					:emit-expand-collapse="emitExpandCollapse"
					:search-visible="searchVisible"
					:dispute-count="globalStore.organization.ActiveDisputes"
					:has-onboarded-cognito-pay-account="hasOnboardedCognitoPayAccount"
					:has-incomplete-cognito-pay-account="hasIncompleteCognitoPayAccount"
					@close-search="$emit('close-search')"
					@search-query="handleSearchQuery"
					@pass-focus="focusFirstItem($event)"
					@payment-clicked="handlePaymentClicked"
				/>
			</div>
			<CFormTreeContainer
				:global-store="globalStore"
				:search-query="searchQuery"
				:on-expand-or-collapse="onExpandOrCollapse"
				class="border-t"
				:class="{ 'border-primary' : scrollPos > 0, 'border-transparent': scrollPos === 0 }"
				@select="handleSelectEntryView"
				@scroll="onScroll"
				@has-scroll="hasScroll = $event"
			/>
			<CNavFooter
				:is-popper="isPopper"
				:is-modal="showOverlay"
				:has-scroll="hasScroll"
				:client-role="globalStore.organization.Role"
				:org-code="globalStore.organization.Code"
				:can-invite-users="globalStore.organization.Plan.IsPaidPlan"
				:can-create-folders="canCreateFolders"
				:can-only-create-forms="canOnlyCreateForms"
				:lost-folders="hasLockedFolders"
				@toggle-open="toggleOpen"
				@close-popper="closePopper"
				@handling-interaction="handlingInteraction"
				@create-folder="handleCreateFolder"
			>
				<CNavMobileFooter
					v-if="isMobile"
					:org-code="globalStore.organization.Code"
					:is-org-owner="isOrgOwner"
					:is-org-admin="isOrgAdmin"
					:is-paid-plan="globalStore.organization.Plan.IsPaidPlan"
					:can-use-tasks="globalStore.organization.Plan.AllowWorkflowTasks"
					:task-count="globalStore.totalTaskCount"
					:has-onboarded-cognito-pay-account="hasOnboardedCognitoPayAccount"
					:has-incomplete-cognito-pay-account="hasIncompleteCognitoPayAccount"
					@payment-clicked="handlePaymentClicked"
				/>
			</CNavFooter>
		</div>

		<CUpsellDialog
			v-if="showEntryViewUpsell"
			:show="showEntryViewUpsell"
			:org-code="globalStore.organization?.Code"
			feature="entryviews"
			@close="showEntryViewUpsell = false"
		/>

		<CognitoPayOnboardingContainer
			v-if="showCognitoPayOnboarding"
			:account-id="cognitoPayOnboardingAccountId"
			@closed="showCognitoPayOnboarding = false"
		/>

		<CognitoPayRequirementDialog
			v-if="showCognitoPayRequirement"
			is-visible
			:reason="cognitoPayRequirementReason"
			:has-incomplete-account="hasIncompleteCognitoPayAccount"
			@closed="showCognitoPayRequirement = false"
			@verified="handleRequirementVerified"
		/>

		<Transition name="fade">
			<div v-if="showOverlay" class="nav__overlay" @click="closePopper" />
		</Transition>
	</div>
</template>

<script lang="ts">
	import { computed, defineAsyncComponent, defineComponent, watch, ref, inject, onMounted, onUnmounted } from 'vue';
	import type { PropType } from 'vue';
	import CNavHeader from './NavHeader.vue';
	import CNavMain from './NavMain.vue';
	import CNavMobileFooter from './NavMobileFooter.vue';
	import CFormTreeContainer from './FormTreeContainer.vue';
	import CNavFooter from './NavFooter.vue';
	import CUpsellDialog from '../dialogs/UpsellDialog.vue';
	import { Draggable } from '@shopify/draggable';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import type { GlobalStore } from 'src/stores/global-store';
	import type { OrganizationMeta } from '@cognitoforms/types/server-types/core/services/organization-meta';
	import { isMobileKey, navStateKey } from 'src/utilities/injection-keys';
	import notify from 'src/utilities/notify';
	import type { SelectEvent } from './FormTreeContainer.vue';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import { ExpandCollapseEvent, ClearSearchEvent, useEventBus } from 'src/views/admin/utils/useEventBus';
	import { focusFirstItem } from 'src/composables/use-keyboard-nav';
	import { getDashboardCognitoPayAccounts, getIncompleteCognitoPayAccounts } from 'src/views/admin/cognito-pay/utils/cognito-pay-utils';
	import { useCognitoPayOnboardingNav } from './use-cognito-pay-onboarding-nav';

	export default defineComponent({
		name: 'CAppNav',
		components: {
			CNavMain,
			CNavMobileFooter,
			CFormTreeContainer,
			CNavFooter,
			CNavHeader,
			CUpsellDialog,
			CognitoPayOnboardingContainer: defineAsyncComponent(() => import(/* webpackChunkName: 'CognitoPayOnboardingContainer' */ '../app/CognitoPayOnboardingContainer.vue')),
			CognitoPayRequirementDialog: defineAsyncComponent(() => import(/* webpackChunkName: 'CognitoPayRequirementDialog' */ '../app/CognitoPayRequirementDialog.vue'))
		},
		props: {
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			},
			searchVisible: { type: Boolean, default: false }
		},
		emits: ['select-entry-view', 'navigate-home', 'show-account-menu', 'close-search'],
		setup(props, { emit }) {
			const navState = inject(navStateKey);
			const isMobile = inject(isMobileKey);

			const toggleOpen = () => navState.setIsOpen(!navState.isOpen.value);

			const organizationMeta = computed<OrganizationMeta>(() => props.globalStore.organization as OrganizationMeta);
			const isOrgOwner = computed(() => checkRole(ClientRole.Owner));
			const isOrgAdmin = computed(() => checkRole(ClientRole.Administrator));
			const canCreateFolders = computed(() => props.globalStore.organization.Plan.AllowCreateFolders);
			const hasLockedFolders = computed(() => props.globalStore.organization.HasFolders && !canCreateFolders.value);

			const searchQuery = ref('');
			const allowUserToSearch = computed(() => props.globalStore.organization.Role !== ClientRole.Guest);
			const hasVisibleFolders = computed(() => {
				if (Object.keys(props.globalStore.folders).length === 0)
					return false;
				else if (!navState.expandedNodes['archived'].includes('archive')) // if the archived section is not expanded, ensure that there is at least one non-archived folder
					return Object.values(props.globalStore.folders).filter(f => !f.IsArchived).length !== 0;
				else
					return true;
			});

			const orgThemeLogo = computed(()=> props.globalStore.orgAppearanceSettings?.LogoLink);

			const handleSearchQuery = (e) => {
				requestAnimationFrame(() => {
					searchQuery.value = e;
				});
			};

			const { emit: emitExpandCollapse, on: onExpandOrCollapse } = useEventBus(ExpandCollapseEvent);
			const everythingIsCollapsed = computed(() => {
				const includeArchive = navState.expandedNodes['archived'].includes('archive'); // only consider archived forms if the archived section is expanded
				let expandedNodes = Array.from(navState.expandedNodes['default']).concat(includeArchive ? Array.from(navState.expandedNodes['archived'].filter(a => a !== 'archive')) : []);

				let noExpandedForms = false;
				if (expandedNodes.length === 0)
					noExpandedForms = true;
				else {
					const expandedFormsInCollapsedFolders = [];
					expandedNodes.forEach((node) => {
						const form = props.globalStore.forms[node];
						const folder = props.globalStore.folders[node];
						if (form) {
							const folderId = form?.FolderId;
							if (folderId) {
								// if it is expanded, but it is in a folder that is collapsed
								const isExpanded = expandedNodes.find(n => n === folderId);
								if (!isExpanded)
									expandedFormsInCollapsedFolders.push(node);
							}
						}
						// if its not a valid form or folder (it was deleted/lost access), remove it from the expandedNodes
						else if (!folder) {
							expandedNodes = expandedNodes.filter(n => n !== node);
						}
					});
					// if all of the expanded nodes are expanded forms in collapsed folders
					noExpandedForms = expandedFormsInCollapsedFolders.length === expandedNodes.length;
				}

				if (expandedNodes.length === 1 && !noExpandedForms) { // handles single view forms being added to expandedNodes
					const formId = expandedNodes[0];
					const form = props.globalStore.forms[formId];
					noExpandedForms = form?.IsSingleViewForm;
				}
				return noExpandedForms;
			});

			const expandCollapseOption = computed(() => hasVisibleFolders.value && everythingIsCollapsed.value ? 'Expand' : 'Collapse');

			const hasOnboardedCognitoPayAccount = computed(() =>
				getDashboardCognitoPayAccounts(props.globalStore.organization?.PaymentAccounts).length > 0);

			const hasIncompleteCognitoPayAccount = computed(() => {
				if (hasOnboardedCognitoPayAccount.value)
					return false;

				return getIncompleteCognitoPayAccounts(props.globalStore.organization?.PaymentAccounts).length > 0;
			});

			const {
				showCognitoPayOnboarding,
				cognitoPayOnboardingAccountId,
				showCognitoPayRequirement,
				cognitoPayRequirementReason,
				handlePaymentClicked,
				handleRequirementVerified
			} = useCognitoPayOnboardingNav({
				orgCode: computed(() => props.globalStore.organization.Code),
				hasIncompleteCognitoPayAccount
			});

			const handlingInteraction = (isInteracting: boolean) => navState.setIsHandlingInteraction(isInteracting);

			const canOnlyCreateForms = computed(() => {
				if (isOrgAdmin.value)
					return false;

				// Check if user can manage any folders
				return !!Object.values(props.globalStore.folders).find(f => f.CanManageFolder && !f.IsArchived);
			});

			const { emit: emitClearSearchEvent } = useEventBus(ClearSearchEvent);

			const handleCreateFolder = async () => {
				// Clear search if present, handled in NavSearch
				emitClearSearchEvent();

				try {
					const folderId = await props.globalStore.createFolder();
					navState.setNodeRenameState(folderId, 'default');
				}
				catch (e) {
					// View as user error is already handled by now
					// Restricted Support User error (although not handled globally) should be ignored here as well
					if (e.message !== 'View As User' && e.message !== 'Restricted Support User')
						notify('You do not have permission to create folders.', 'error');
				}
			};

			const showEntryViewUpsell = ref(false);
			const handleSelectEntryView = (event: SelectEvent) => {
				// Prevent navigating to saved view if locked
				if (event.isLocked) {
					showEntryViewUpsell.value = true;
					return;
				}

				emit('select-entry-view', event);
			};

			const leaveTimeout = ref<number | null>(null);

			const handleMouseEnter = () => {
				if (leaveTimeout.value) {
					clearTimeout(leaveTimeout.value);
					leaveTimeout.value = null;
				}
			};

			const handleMouseLeave = () => {
				if (navState.isPopper.value && !navState.showOverlay.value) {
					leaveTimeout.value = window.setTimeout(() => {
						navState.setIsPopper(false);
						leaveTimeout.value = null;
					}, 500);
				}
			};

			const popperTimeout = ref<NodeJS.Timeout>(null);
			const showPopper = (showOverlay) => {
				if (showOverlay) {
					clearTimeout(popperTimeout.value);
					navState.setIsPopper(true, true);
				}
				else
					popperTimeout.value = setTimeout(() => navState.setIsPopper(true), 500);
			};

			const hidePopper = () => clearTimeout(popperTimeout.value);

			const closePopper = () => {
				navState.setIsPopper(false);
			};

			const isMouseDown = ref(false);
			const hoverDisabled = ref(false);

			const handleHoverExpandMouseEnter = () => {
				if (isMouseDown.value)
					hoverDisabled.value = true;

				if (hoverDisabled.value)
					return;

				showPopper(false);
			};

			const handleHoverExpandMouseLeave = () => {
				hidePopper();
				hoverDisabled.value = false;
			};

			const onGlobalMouseDown = () => {
				isMouseDown.value = true;
			};

			const onGlobalMouseUp = () => {
				isMouseDown.value = false;
			};

			// Had to use pointerup because Element prevents mouseup from working in some of their components (i.e. slider)
			onMounted(() => {
				window.addEventListener('pointerdown', onGlobalMouseDown);
				window.addEventListener('pointerup', onGlobalMouseUp);
			});

			onUnmounted(() => {
				window.removeEventListener('pointerdown', onGlobalMouseDown);
				window.removeEventListener('pointerup', onGlobalMouseUp);
			});

			const scrollPos = ref(0);
			const onScroll = (e: Event) => {
				scrollPos.value = (e.target as HTMLElement).scrollTop;
			};

			// Initialize sidebar draggability
			const navRef = ref<HTMLElement>(null);
			watch(() => navRef.value, (navRef) => {
				const draggable = new Draggable(navRef, {
					draggable: '.nav__drag-handle',
					delay: 0
				});

				// Update width on drag move; do not persist width to local storage
				draggable.on('drag:move', e => navState.setWidth(e.sensorEvent.clientX, false));

				// Persist width to local storage on drag stop
				draggable.on('drag:stop', e => navState.setWidth(navState.navWidth.value, true));
			});

			return {
				isMobile,
				toggleOpen,
				organizationMeta,
				orgThemeLogo,
				isOrgOwner,
				isOrgAdmin,
				canCreateFolders,
				hasLockedFolders,
				handlingInteraction,
				canOnlyCreateForms,
				handleCreateFolder,
				showEntryViewUpsell,
				handleSelectEntryView,
				handleMouseEnter,
				handleMouseLeave,
				showPopper,
				hidePopper,
				closePopper,
				handleHoverExpandMouseEnter,
				handleHoverExpandMouseLeave,
				scrollPos,
				onScroll,
				navRef,
				isPopper: computed(() => navState.isPopper.value),
				isOpen: computed(() => navState.isOpen.value),
				showOverlay: computed(() => navState.showOverlay.value),
				hasScroll: ref(false),
				handleSearchQuery,
				searchQuery,
				allowUserToSearch,
				expandCollapseOption,
				hasVisibleFolders,
				hasOnboardedCognitoPayAccount,
				hasIncompleteCognitoPayAccount,
				showCognitoPayOnboarding,
				cognitoPayOnboardingAccountId,
				showCognitoPayRequirement,
				cognitoPayRequirementReason,
				handlePaymentClicked,
				handleRequirementVerified,
				emitExpandCollapse,
				onExpandOrCollapse,
				focusFirstItem
			};
		}
	});
</script>

<style lang="scss">
.tooltip--nav {
	pointer-events: none;
}

html {

	&.is-windows {

		.tooltip--nav-shortcut__text--mac {
			display: none;
		}
	}

	&.is-mac {

		.tooltip--nav-shortcut__text--windows {
			display: none;
		}
	}

	&.is-android,
	&.is-ios {

		.tooltip--nav-shortcut {
			display: none;
		}
	}
}

.nav {
	$z-nav: 1;
	$z-sticky-header: 1;
	$z-popper-target: 2;
	$z-drag-handle: 2;
	container-type: inline-size;
	width: var(--nav-width);

	[data-whatinput='keyboard'] .admin--desktop & *:focus {
		outline: 1px solid var(--outline-focused);
	}

	&__sticky {
		z-index: $z-sticky-header;
		padding-left: env(safe-area-inset-left, 0px);
	}

	&__sticky--align {
		height: $AN-app-heading-height;
	}

	&__side-menu {
		position: relative;
		z-index: $z-nav;
		width: 100%;
		outline: 1px solid var(--border-primary);

		&--transition {
			opacity: 0;
			transition: .5s;
		}

		&--mobile {
			outline-style: none;
		}
	}

	&__drag-handle {
		right: -3px;
		z-index: $z-drag-handle;
		width: 5px;
		outline: 0;
		cursor: col-resize;
		transition: background-color .2s .2s;

		&.draggable-source--is-dragging,
		&:hover {
			background: var(--accent-5);
		}

		@include b-down($b9) {
			right: -8px;
			width: 12px;

			&.draggable-source--is-dragging,
			&:hover {
				background: linear-gradient(to right, transparent, transparent 4px, var(--accent-5) 4px, var(--accent-5) 9px, transparent 9px);
			}
		}

		.admin--nav-collapsed:not(.admin--nav-overlay) & {
			display: none;
		}
	}

	&__grace-area {
		right: -$space__3;
		top: 0;
		width: $space__3;

		// Grace area being interactable before side menu slides out causes issues
		animation: disable-interaction .2s forwards;

		@keyframes disable-interaction {

			0% {
				pointer-events: none;
			}

			100% {
				pointer-events: all;
			}
		}
	}

	&__hover-expand {
		z-index: $z-popper-target;
		width: 1px;
		transform: translateX(var(--nav-width));
	}

	.scrollbar {
		scrollbar-gutter: stable;
	}

	&__overlay {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		top: 0;
		z-index: 0;
		min-width: calc(100vw + var(--nav-width)); // fixes Safari bug
		background: var(--bg-overlay);
	}
}

</style>